import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps, NextPage, Redirect } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { lastValueFrom } from 'rxjs';

import { showConfirmModal } from '@/redux/slices/gui.slice';
import { initializeStore } from '../redux/initialize-store';

import { useAppDispatch, useAppSelector } from '@/redux';
import { getI18NextConfigForLocale } from '@/utils/locales';
import { prefetchLayout } from '@/utils/prefetchLayout';
import { sanitizeObject } from '@/utils/millesimaUtils';
import { HomePageLayoutModel, SlideModel } from '@/models';
import { useServices } from '@/services/useServices';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';

import Carousel from '@/components/layout/carousel/Carousel';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import DynamicLayout from '@/components/layout/DynamicLayout';
import buildLinkedDataTs from '@/linkedData/trustedShop-lkd';
import JsonLd from '@/components/common/JsonLd';

import styles from './index.module.scss';
import { BentoModel } from '@/models/Bento.model';
import Bento from '@/components/marketing/Bento';

const NAMESPACES = [
  NS.COMMON,
  NS.CHECKOUT,
  NS.ALTER,
  NS.COUNTRIES
];

interface IndexPageProps {
  bento: BentoModel;
  slides?: SlideModel[];
  homepageLayout: HomePageLayoutModel;
  ratingValue: number;
  ratingCount: number;
}

export const Index: NextPage<IndexPageProps> = ({ bento, slides, homepageLayout, ratingValue, ratingCount }) => {
  const { isMobileDevice } = useAppSelector(({ gui }) => gui);
  const storeConf = useAppSelector(({ root }) => root.storeConf);

  const jsonTs = buildLinkedDataTs(storeConf, ratingValue, ratingCount);
  const { storeId, description, privateWebsite } = storeConf;
  const { datalayerService, slideService } = useServices();
  const { mappedBreakpoint: breakpoint, isMobile } = useScreenSizeContext();
  const [slidesHp, setSlidesHp] = useState<SlideModel[]>();
  const imgSrcToPreload = getImgSrc(slides, isMobileDevice) as [];
  const { t } = useTranslation(NAMESPACES);
  const dispatch = useAppDispatch();
  const router = useRouter();

  const slidesFunc = useMemo(() => getSlidesFunc(isMobileDevice, isMobile), [isMobileDevice, breakpoint]);

  useEffect(() => {
    if (!slidesHp) {
      if (slides) {
        setSlidesHp(slides);
      } else {
        lastValueFrom((slideService as any)[slidesFunc](storeId)).then((slides: any) => {
          setSlidesHp(slides);
        });
      }
    }
  }, [slides, storeId, slidesHp, isMobileDevice, isMobile]);

  useEffect(() => {
    datalayerService.pushPageName('HomePage', 'HomePage');

    // Display the modal if the param is present
    if (router.query.primeurSubscription) {
      let modalProps = {
        title: t('forms.confirmation'),
        content: t('alert.signupSuccess'),
        confirm: t('btn.ok')
      };

      if (router.query.primeurSubscription === 'error') {
        modalProps = {
          title: t('alert.error'),
          content: t('errors.genericErrorMessage.main'),
          confirm: t('btn.ok')
        };
      }

      dispatch(showConfirmModal({ content: modalProps }));
    }
  }, []);

  return (
    <div className={styles['millesima-index']}>
      <JsonLd data={jsonTs}/>

      <MetaTagsMillesima
        title={t('title')}
        metaDesc={description}
        home
        privateWebsite={privateWebsite}
        imgSrcToPreload={imgSrcToPreload}
      />

      <div className={`${styles['dynamic-layout-section']} ${styles['full-width']}`}>
        <Bento bento={bento} />
        <Carousel slides={slidesHp || slides} isMobile={isMobile} isMobileDevice={isMobileDevice} />
      </div>

      <DynamicLayout
        componentList={homepageLayout?.containers}
        isMobileDevice={isMobileDevice}
      />
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<IndexPageProps> = (async (ctx) => {
  const store = initializeStore({ ssrContext: ctx });
  const layoutOptions = await prefetchLayout(ctx, store);
  const result = layoutOptions as { redirect: Redirect };

  // We want to redirect the user to another page
  if (result.redirect) {
    return result;
  }

  const { root: { storeConf, ratingValue, ratingCount }, gui: { isMobileDevice } } = store.getState();
  const { locale } = storeConf || ctx;
  const translate = await serverSideTranslations(locale, NAMESPACES, getI18NextConfigForLocale(locale));
  const { bentoService, slideService, homepageLayoutService } = store.services;
  const { storeId } = storeConf;
  let homepageLayout: HomePageLayoutModel[] = [];
  let slides: SlideModel[] = [];
  let bento: BentoModel;

  try {
    slides = await lastValueFrom(slideService[getSlidesFunc(isMobileDevice, false)](storeId));
  } catch (error) {
    console.error('Index.getServerSideProps slides error', error);
  }

  try {
    bento = await lastValueFrom(bentoService.getBentoByStoreId(storeId));
  } catch (error) {
    console.error('Index.getServerSideProps bento error', error);
  }

  try {
    homepageLayout = await lastValueFrom(homepageLayoutService.getActiveByStoreId(storeConf.storeId));
  } catch (error) {
    console.error('Index.getInitialProps homepageLayout error', error);
  }

  return {
    props: sanitizeObject({
      ...translate,
      layoutOptions,
      ratingValue,
      ratingCount,
      slides,
      bento,
      homepageLayout,
      initialReduxState: store.getState()
    })
  };
});

const getSlidesFunc = (isMobileDevice: boolean, isMobileView: boolean): string => (
  (isMobileDevice || isMobileView) ?
    'getCurrentSlideMData' :
    'getCurrentSlideData'
);

const getImgSrc = (slides?: SlideModel[], isMobileDevice = false): string[] => {
  if (slides?.length) {
    const imgSrc = isMobileDevice ? slides[0].imgS : slides[0].imgXl;
    if (imgSrc) {
      return [imgSrc];
    }
  }
  return [];
};

export default Index;
